import React, { useCallback, useEffect, useState } from 'react';
import { useLocalStorage, deleteFromStorage } from '@rehooks/local-storage';
import { Button, Col, Container, Row, Table, Spinner } from 'react-bootstrap';
import { CredentialsModal } from './components/credentials-modal/credentials-modal';
import { CreateEventModal } from './components/create-event-modal/create-event-modal';
import { GuestLinkModal } from './components/guest-link-modal/guest-link-modal';
import { Event } from './models/event';
import { preventedDefault } from './utils/events';
import { useHostAccessTokenQuery } from './hooks/data/use-host-acces-token-query';
import {
  LOCALSTORAGE_CLIENT_SECRET,
  LOCALSTORAGE_USER_ID,
  LOCALSTORAGE_CLIENT_ID,
  LOCALSTORAGE_EVENTS,
} from './constants';
import styles from './App.module.scss';

export const App = () => {
  const [
    isCreateEventModalVisible,
    setIsCreateEventModalVisible,
  ] = useState<boolean>(false);

  const [guestLink, setGuestLink] = useState<string>('');

  const [userId] = useLocalStorage<number>(LOCALSTORAGE_USER_ID);
  const [clientId] = useLocalStorage<string>(LOCALSTORAGE_CLIENT_ID);
  const [clientSecret] = useLocalStorage<string>(LOCALSTORAGE_CLIENT_SECRET);
  const [events] = useLocalStorage<Event[]>(LOCALSTORAGE_EVENTS);

  const endSession = useCallback(() => {
    [
      LOCALSTORAGE_CLIENT_SECRET,
      LOCALSTORAGE_USER_ID,
      LOCALSTORAGE_CLIENT_ID,
      LOCALSTORAGE_EVENTS,
    ].forEach(deleteFromStorage);
  }, []);

  const isAppReady = Boolean(userId && clientId && clientSecret);

  const {
    data: hostAccessTokenData,
    status: hostAccessTokenDataStatus,
    error: hostAccessTokenDataError,
  } = useHostAccessTokenQuery(
    { userId, clientId, clientSecret },
    {
      enabled: isAppReady,
    },
  );

  useEffect(() => {
    if (hostAccessTokenDataError) {
      endSession();
    }
  }, [hostAccessTokenDataError, endSession]);

  return (
    <>
      <CredentialsModal isVisible={!userId} />

      {hostAccessTokenDataStatus === 'loading' && (
        <div className={styles.spinnerContainer}>
          <Spinner animation="border" variant="primary" />
        </div>
      )}

      {isAppReady && hostAccessTokenData && (
        <>
          <CreateEventModal
            isVisible={isCreateEventModalVisible}
            onClose={() => setIsCreateEventModalVisible(false)}
          />

          <GuestLinkModal
            isVisible={!!guestLink}
            onClose={() => setGuestLink('')}
            guestLink={guestLink}
          />

          <Container className={styles.container}>
            <Row>
              <Col sm={8}>
                <h1>Events</h1>
              </Col>
              <Col sm={4}>
                <Button
                  variant="primary"
                  className={styles.button}
                  onClick={() => setIsCreateEventModalVisible(true)}
                >
                  Create event
                </Button>
                <Button
                  variant="outline-danger"
                  className={styles.button}
                  onClick={endSession}
                >
                  Exit
                </Button>
              </Col>
            </Row>

            <Row>
              <Col>
                <Table>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Host</th>
                      <th>Guest</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(events || []).map((event) => (
                      <tr key={event.eventId}>
                        <td>{event.title}</td>
                        <td>
                          <a
                            target="_blank"
                            rel="noreferrer"
                            href={`${event.hostUrl}#accessToken=${hostAccessTokenData?.accessToken}`}
                          >
                            Enter Live Studio
                          </a>
                        </td>
                        <td>
                          <a
                            href={event.guestUrl}
                            onClick={preventedDefault(() =>
                              setGuestLink(event.guestUrl),
                            )}
                          >
                            Invite guests
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Col>
            </Row>
          </Container>
        </>
      )}
    </>
  );
};
